import React, { useEffect, useState } from "react";
import { Loader2, Newspaper, Flame, Clock, ExternalLink } from "lucide-react";
import { fetchCompanyNews } from "@/lib/api";

const _timeAgo = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * AnalysisNewsCard — ticker-specific Finnhub news for the Analyze deep-dive.
 *
 * Pulls /api/market/news/{symbol}, ranks by impact_score, shows top 6 headlines
 * with source, time-ago, and a Flame icon on the highest-impact item.
 */
export const AnalysisNewsCard = ({ symbol }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchCompanyNews(symbol, 8)
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch((e) => {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || "Could not load news");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol]);

  return (
    <div
      className="rounded-xl border border-white/10 bg-[#070710] p-4"
      data-testid="analysis-news-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Newspaper className="w-4 h-4 text-[#3FE5DC]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#3FE5DC]">
          Latest news · {symbol}
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-white/35">
          Past 7 days
        </span>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-white/55 py-3">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {symbol} news…
        </div>
      )}
      {err && (
        <div className="text-[11.5px] text-[#FF3B30] py-2">{err}</div>
      )}
      {!loading && !err && items.length === 0 && (
        <div className="text-[12px] text-white/45 py-2">
          No recent news for {symbol}.
        </div>
      )}
      {!loading && !err && items.length > 0 && (
        <div className="space-y-2">
          {items.slice(0, 6).map((it, i) => {
            const isHot = i === 0 || it.impact_score >= 25;
            return (
              <a
                key={it.id}
                href={it.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`analysis-news-row-${it.id}`}
                className="group block rounded-md border border-white/8 bg-white/[0.02] hover:border-white/22 hover:bg-white/[0.04] px-3 py-2 transition"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {isHot && <Flame className="w-3 h-3 text-[#FF9500]" />}
                      <span className="text-[10px] uppercase tracking-[0.14em] text-white/55 truncate">
                        {it.source}
                      </span>
                      <span className="text-[9.5px] text-white/35 inline-flex items-center gap-1 shrink-0">
                        <Clock className="w-2.5 h-2.5" /> {_timeAgo(it.datetime)}
                      </span>
                    </div>
                    <div className="text-[12px] text-white/85 leading-snug group-hover:text-white">
                      {it.headline}
                    </div>
                  </div>
                  <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-[#3FE5DC] shrink-0 mt-1" />
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};
